import { useCallback, useEffect, useMemo, useRef } from "react";
import { scheduleDraftDataService } from "../../../services/scheduleDraftDataService";
import { buildFlightNumberSuggestionKey, buildFlightNumberSuggestionPair, buildFlightNumberSuggestionPairs } from "./flightNumberSuggestions";

const FLIGHT_NUMBER_SUGGESTION_DEBOUNCE_MS = 250;
const COMBO_FIELDS = new Set(["aircraft_reg", "departure_airport", "arrival_airport"]);

function indexSuggestionResponse(response) {
    const suggestions = new Map();
    (response?.pairs || []).forEach((item) => {
        const key = buildFlightNumberSuggestionKey(item?.aircraft_reg, item?.dep, item?.arr);
        if (key) {
            suggestions.set(key, String(item?.suggested_flight_number || "").trim());
        }
    });
    return suggestions;
}

function groupChangesByFlight(changeSet) {
    const changesByFlight = new Map();
    (changeSet?.changes || []).forEach((change) => {
        const flightId = String(change?.flightId ?? change?.flight_id ?? "");
        if (!flightId) {
            return;
        }
        const changes = changesByFlight.get(flightId) || [];
        changes.push(change);
        changesByFlight.set(flightId, changes);
    });
    return changesByFlight;
}

function applyChangesToFlight(flight, changes) {
    return (changes || []).reduce((nextFlight, change) => {
        if (!change?.field) {
            return nextFlight;
        }
        return {
            ...nextFlight,
            [change.field]: change.after,
        };
    }, { ...(flight || {}) });
}

export default function useFlightNumberSuggestions({ draftId, rows }) {
    const cacheRef = useRef(new Map());
    const loadingRef = useRef(new Map());
    const debounceTimersRef = useRef(new Map());
    const evictedKeysRef = useRef(new Set());
    const draftIdRef = useRef(draftId || null);
    const pairs = useMemo(() => buildFlightNumberSuggestionPairs(rows), [rows]);
    const pairSignature = useMemo(
        () => pairs.map((pair) => buildFlightNumberSuggestionKey(pair.aircraft_reg, pair.dep, pair.arr)).join("|"),
        [pairs],
    );

    useEffect(() => {
        if (draftIdRef.current === (draftId || null)) {
            return;
        }

        draftIdRef.current = draftId || null;
        cacheRef.current.clear();
        loadingRef.current.clear();
        evictedKeysRef.current.clear();
        debounceTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
        debounceTimersRef.current.clear();
    }, [draftId]);

    const fetchPairs = useCallback(
        async (pairsToFetch) => {
            if (!draftId || !pairsToFetch?.length) {
                return new Map();
            }

            try {
                const response = await scheduleDraftDataService.getFlightNumberSuggestions(draftId, pairsToFetch);
                const nextSuggestions = indexSuggestionResponse(response);
                pairsToFetch.forEach((pair) => {
                    const key = buildFlightNumberSuggestionKey(pair.aircraft_reg, pair.dep, pair.arr);
                    if (key && !evictedKeysRef.current.has(key)) {
                        cacheRef.current.set(key, nextSuggestions.get(key) || "");
                    }
                });
                return nextSuggestions;
            } catch {
                return new Map();
            }
        },
        [draftId],
    );

    const fetchMissingPairs = useCallback(
        (candidatePairs) => {
            const pairsToFetch = [];
            const keysToFetch = [];
            (candidatePairs || []).forEach((pair) => {
                const key = buildFlightNumberSuggestionKey(pair.aircraft_reg, pair.dep, pair.arr);
                if (!key || cacheRef.current.has(key) || loadingRef.current.has(key) || evictedKeysRef.current.has(key)) {
                    return;
                }
                pairsToFetch.push(pair);
                keysToFetch.push(key);
            });

            if (!pairsToFetch.length) {
                return Promise.resolve(new Map());
            }

            const requestPromise = fetchPairs(pairsToFetch).finally(() => {
                keysToFetch.forEach((key) => loadingRef.current.delete(key));
            });
            keysToFetch.forEach((key) => {
                loadingRef.current.set(key, requestPromise.then(() => cacheRef.current.get(key) || ""));
            });
            return requestPromise;
        },
        [fetchPairs],
    );

    useEffect(() => {
        void fetchMissingPairs(pairs);
    }, [fetchMissingPairs, pairSignature, pairs]);

    useEffect(() => () => {
        debounceTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
        debounceTimersRef.current.clear();
    }, []);

    const getSuggestion = useCallback((aircraftReg, dep, arr) => {
        const key = buildFlightNumberSuggestionKey(aircraftReg, dep, arr);
        return key && cacheRef.current.has(key) ? cacheRef.current.get(key) || "" : "";
    }, []);

    const loadSuggestion = useCallback(
        (aircraftReg, dep, arr) => {
            const pair = buildFlightNumberSuggestionPair(aircraftReg, dep, arr);
            if (!pair) {
                return Promise.resolve("");
            }

            const key = buildFlightNumberSuggestionKey(pair.aircraft_reg, pair.dep, pair.arr);
            evictedKeysRef.current.delete(key);
            if (cacheRef.current.has(key)) {
                return Promise.resolve(cacheRef.current.get(key) || "");
            }
            if (loadingRef.current.has(key)) {
                return loadingRef.current.get(key);
            }

            const requestPromise = new Promise((resolve) => {
                const timerId = window.setTimeout(() => {
                    debounceTimersRef.current.delete(key);
                    void fetchPairs([pair]).then(() => resolve(cacheRef.current.get(key) || "")).finally(() => {
                        loadingRef.current.delete(key);
                    });
                }, FLIGHT_NUMBER_SUGGESTION_DEBOUNCE_MS);
                debounceTimersRef.current.set(key, timerId);
            });
            loadingRef.current.set(key, requestPromise);
            return requestPromise;
        },
        [fetchPairs],
    );

    const invalidateCombo = useCallback((aircraftReg, dep, arr) => {
        const key = buildFlightNumberSuggestionKey(aircraftReg, dep, arr);
        if (!key) {
            return;
        }

        cacheRef.current.delete(key);
        evictedKeysRef.current.add(key);
        const timerId = debounceTimersRef.current.get(key);
        if (timerId) {
            window.clearTimeout(timerId);
            debounceTimersRef.current.delete(key);
        }
        loadingRef.current.delete(key);
    }, []);

    const invalidateFlight = useCallback(
        (flight) => {
            invalidateCombo(flight?.aircraft_reg, flight?.departure_airport, flight?.arrival_airport);
        },
        [invalidateCombo],
    );

    const invalidateChangedCombos = useCallback(
        (changeSet, beforeFlightsById) => {
            groupChangesByFlight(changeSet).forEach((changes, flightId) => {
                if (!changes.some((change) => COMBO_FIELDS.has(change?.field))) {
                    return;
                }

                const beforeFlight = beforeFlightsById?.get(String(flightId)) || {};
                const afterFlight = applyChangesToFlight(beforeFlight, changes);
                invalidateFlight(beforeFlight);
                invalidateFlight(afterFlight);
            });
        },
        [invalidateFlight],
    );

    return useMemo(() => ({
        getSuggestion,
        loadSuggestion,
        invalidateChangedCombos,
    }), [getSuggestion, invalidateChangedCombos, loadSuggestion]);
}
